import { FC, useState, useEffect } from "react";
import { Box, Modal, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { api } from "@/helper/api";
import GenericDateTime from "@/components/dateTime/genericDateTime";
import dayjs from "dayjs";
import "dayjs/locale/de";
import { IDropDownOption } from "@/types/DropDown";
import { DropDownSubjectSearch } from "@/components/forms/inputs/dropDownSubjectSearch";
import DropDown from "@/components/forms/inputs/dropDown";
import { SubjectSearchSelect } from "@/components/subjectSearch/subjectSearchSelect";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { getTranslatedOptionObject } from "@/helper/statusOptions";
import styles from "@/components/subjectOverview/subjectOverviewAppointmentCreateModal.module.css";
import { useTranslation } from "react-i18next";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Controller, FormProvider, useForm } from "react-hook-form";
import CrudField from "@/components/tables/fields/CrudField";
import GenericNotification from "@/components/notification/genericNotification";
import { useSelector } from "react-redux";
import { RootState } from "@/components/state/store";
import { FormFieldLabel } from "@/components/forms/inputs/formFieldLabel";
import { useModalDismiss } from "@/helper/customHooks/useModalDismiss";
import {
  DEFAULT_KAER_TEAM_ID,
  KAER_COMPANY_SUBJECT_ID,
} from "@/helper/systemIds";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";
import useFormSubmission from "@/helper/customHooks/useFormSubmission";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import { isCompanySubjectSelection } from "@/helper/quickSearchSelection";
import {
  OCCUPATIONAL_MEDICINE_COMPANY_APPOINTMENT_CONFIRMATION_KEY,
  isOccupationalMedicineCategory,
} from "@/components/appointment/occupationalMedicineCompanyAppointmentConfirmation";

interface Props {
  open: boolean;
  onClose: () => void;
  detailedSubject?: any;
  onAppointmentCreated?: () => void;
  openNewlyCreatedAppointmentInNewTab?: boolean;
  usePersistedSubjectSearchValue?: boolean;
}

const SubjectOverviewAppointmentCreateModal: FC<Props> = ({
  open,
  onClose,
  detailedSubject,
  onAppointmentCreated,
  openNewlyCreatedAppointmentInNewTab = true,
  usePersistedSubjectSearchValue = false,
}): JSX.Element => {
  const { t } = useTranslation();
  const { showConfirmationModalAsync } = useConfirmationModal();
  const [meSubjectDefault, setMeSubjectDefault] = useState<any>(undefined);
  const [date, setDate] = useState<any>(dayjs());

  const persistedSubjectSearchValue = useSelector(
    (state: RootState) => state.subjectSearch,
  );

  const getInitialSubjectValue = () => {
    if (detailedSubject?.id) {
      return {
        value: detailedSubject.id,
        label: detailedSubject.name,
      };
    }
    if (
      usePersistedSubjectSearchValue &&
      persistedSubjectSearchValue &&
      Object.keys(persistedSubjectSearchValue).length !== 0
    ) {
      return persistedSubjectSearchValue;
    }
    return { value: "", label: "" };
  };

  const [subject, setSubject] = useState<any>(getInitialSubjectValue());
  const [team, setTeam] = useState<any>("");
  const [teamOptions, setTeamOptions] = useState<IDropDownOption[]>([]);
  const [executedBy, setExecutedBy] = useState<any>("");
  const [executedByOptions, setExecutedByOptions] = useState<any>([]);
  const [assistedBy, setAssistedBy] = useState<any>("");
  const [assistedByOptions, setAssistedByOptions] = useState<any>([]);
  const [appointmentStatus, setAppointmentStatus] = useState<IDropDownOption>();
  const [onSite, setOnsite] = useState<boolean | null>(null);

  const [statusOptions, setStatusOptions] = useState<any[]>([]);
  const [buildingOptionsForSubject, setBuildingOptionsForSubject] =
    useState<any>([]);
  const [selectedBuilding, setSelectedBuilding] = useState<any>();
  const [formValues, setFormValues] = useState<any>({
    contact_name: "",
    contact_phone: "",
    contact_email: "",
    parking_situation: "",
    additional_information: "",
  });

  const [appointmentId, setAppointmentId] = useState<string | null>(null);
  const [categoryOptions, setCategoryOptions] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<any>(null);
  const [selectedExaminations, setSelectedExaminations] = useState<any[]>([]);

  const hostname = window.location.hostname;
  const subdomain = hostname.split(".")[0];

  const { handleError, errorModal } = useErrorModal(undefined, {
    confirmButtonText: t("ok_capital"),
  });
  const [validationErrors, setValidationErrors] = useState<{
    date?: string;
    subject?: string;
    team?: string;
    appointmentStatus?: string;
    on_site?: string;
    category?: string;
    examination?: string;
    [key: string]: string | undefined;
  }>({});

  const [isBatchCreating, setIsBatchCreating] = useState(false);
  const [viewMode, setViewMode] = useState<"subject" | "company">("subject");
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField<Record<string, never>, string>({
      fieldOrder: [],
    });

  const onSiteOptions = [
    { label: t("yes"), value: true },
    { label: t("no"), value: false },
  ];

  const userCreationFields = [
    { name: "first_name", label: t("first_name"), required: true },
    { name: "last_name", label: t("last_name"), required: true },
    {
      name: "sex",
      label: t("gender"),
      required: true,
      errorMessage: t("field_required"),
      selectOptions: [
        { value: "male", label: t("male") },
        { value: "female", label: t("female") },
        { value: "diverse", label: t("diverse") },
      ],
    },
    { name: "email", label: t("email"), required: true },
    { name: "phone", label: t("phone"), required: false },
    {
      name: "department_set",
      label: t("departments"),
      required: false,
      multiselect: true,
      entity: "department",
    },
    {
      name: "birth_date",
      label: t("birth_date"),
      required: true,
      datePicker: true,
    },
    {
      name: "is_analog",
      label: t("analog_user"),
      required: false,
      selectOptions: [
        { value: "false", label: t("no") },
        { value: "true", label: t("yes") },
      ],
    },
    {
      name: "preferred_language",
      label: t("preferred_language"),
      required: false,
      selectOptions: [
        { value: "de", label: t("language_german") },
        { value: "en", label: t("language_english") },
      ],
    },
    { name: "worker_id", label: t("worker_id"), required: false },
  ];

  const userFormMethods = useForm({
    mode: "onChange",
    defaultValues: {
      first_name: "",
      last_name: "",
      sex: undefined,
      email: "",
      phone: undefined,
      department_set: [],
      birth_date: "",
      is_analog: undefined,
      preferred_language: undefined,
      worker_id: undefined,
    },
    resolver: (values) => ({ values, errors: {} }),
  });

  const resetForm = () => {
    setDate(dayjs());
    setSubject(getInitialSubjectValue());
    setTeam("");
    setExecutedBy("");
    setAssistedBy("");
    setAppointmentStatus(undefined);
    setSelectedBuilding(undefined);
    setFormValues({
      contact_name: "",
      contact_phone: "",
      contact_email: "",
      parking_situation: "",
      additional_information: "",
    });
    setOnsite(null);
    setSelectedCategory(null);
    setSelectedExaminations([]);
    setAppointmentId(null);
    setValidationErrors({});
    userFormMethods.reset();
  };

  useEffect(() => {
    if (!open) return;
    getMe();
    getTeams();
    getUser();
    getAppointmentStatusOptions();
    getMainCategories();
  }, []);

  const getMe = () => {
    api.genericApiRequest({
      entity: "me",
      method: "get",
      successHandler: (res: any) => {
        setMeSubjectDefault(res.data?.person?.subject ?? undefined);
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  };

  const getTeams = () => {
    api.genericApiRequest({
      entity: "team",
      method: "get",
      successHandler: (res: any) => {
        const teams = [];
        for (const key in res.data.results) {
          const item = res.data.results[key];
          const allTeams = { value: item.id, label: item.name };
          teams.push(allTeams);
        }
        setTeamOptions(teams);

        // Set default team to Kaer team
        const kaerTeam = teams.find(
          (team: any) => team.value === DEFAULT_KAER_TEAM_ID,
        );
        if (kaerTeam) {
          setTeam(kaerTeam);
        }
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  };

  const getUser = () => {
    const additionalUserParameters: any = {};
    additionalUserParameters["impersonate_subject"] = KAER_COMPANY_SUBJECT_ID;
    additionalUserParameters["is_active"] = "true";

    api.genericApiRequest({
      entity: "user",
      method: "get",
      parametersToRender: {
        additionalUrlParameters: additionalUserParameters,
        depth: "0",
      },
      successHandler: (res: any) => {
        const tempUsers = [];
        for (const key in res.data.results) {
          const item = res.data.results[key];
          const allUsers = {
            value: item.subject_id,
            label: item.last_name + ", " + item.first_name,
          };
          tempUsers.push(allUsers);
        }
        setExecutedByOptions(
          tempUsers.sort((a: any, b: any) => a.label.localeCompare(b.label)),
        );
        setAssistedByOptions(
          tempUsers.sort((a: any, b: any) => a.label.localeCompare(b.label)),
        );
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  };
  const getMainCategories = () => {
    api.genericApiRequest({
      entity: "mainCategory",
      method: "get",
      successHandler: (res: any) => {
        const options = res.data.results.map((item: any) => ({
          value: item.id,
          label: item.name,
          trace_id: item.trace_id,
        }));
        setCategoryOptions(options);
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  };

  const handleSetError = (error: any) => {
    handleError(error);
  };

  const fetchBuildings = (additionalUrlParameters: any) => {
    api.genericApiRequest({
      entity: "building",
      method: "get",
      parametersToRender: {
        depth: "0",
        additionalUrlParameters: additionalUrlParameters,
      },
      successHandler: (res: any) => {
        const data = res.data.results;
        const tempBuildingOptions = [];
        for (const key in data) {
          const building = data[key];
          const value = building.id;
          const label =
            building.name +
            ` (${building?.street_house}, ${building?.zip} ${building?.city} )`;
          tempBuildingOptions.push({
            value: value,
            label: label,
            contact_name: building.contact_name,
            contact_email: building.contact_email,
            contact_phone: building.contact_phone,
            parking_situation: building.parking_situation,
            additional_information: building.additional_information,
          });
        }
        setBuildingOptionsForSubject(tempBuildingOptions);
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  };

  useEffect(() => {
    if (!open || !subject?.value) {
      setBuildingOptionsForSubject([]);
      return;
    }

    const fetchForCompany = () => fetchBuildings({ subject_id: subject.value });

    api.genericApiRequest({
      entity: "user",
      method: "get",
      parametersToRender: {
        additionalUrlParameters: { subject_id: subject.value },
        depth: "1",
      },
      successHandler: (userRes: any) => {
        const users = userRes.data.results || [];
        if (users.length > 0) {
          const companyId = users[0].company_id;
          if (companyId) {
            fetchBuildings({
              subject__company: companyId,
              subject__is_company: "true",
            });
          } else {
            setBuildingOptionsForSubject([]);
          }
        } else {
          fetchForCompany();
        }
      },
      failHandler: (err: any) => {
        handleError(err);
        fetchForCompany();
      },
    });
  }, [open, subject?.value]);

  useEffect(() => {
    if (appointmentId) {
      fetchCategoriesAndExaminations();
    }
  }, [appointmentId]);

  const fetchCategoriesAndExaminations = () => {
    api.genericApiRequest({
      entity: "appointment",
      method: "get",
      entityId: appointmentId,
      parametersToRender: { depth: "1", test: "2222" },
      successHandler: (res: any) => {
        const appointment = res.data;
        const raw = appointment.examination_set || [];
        const mapped = raw.map((item: any) => ({
          value: item.id,
          label: item.name,
        }));
        setSelectedExaminations(mapped);
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  };

  const getAppointmentStatusOptions = () => {
    const additionalRouteParts: any = {};
    additionalRouteParts["available_statuses"] = "/";
    api.genericApiRequest({
      entity: "appointment",
      method: "get",
      additionalRouteParts: additionalRouteParts,
      successHandler: (res: any) => {
        const tempStatusOptionArray = res.data.results.map((item: any) =>
          getTranslatedOptionObject(item, true),
        );
        setStatusOptions(tempStatusOptionArray);

        const openStatus = tempStatusOptionArray.find(
          (option: any) => option.value === "open",
        );
        if (openStatus) {
          setAppointmentStatus(openStatus);
        }
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  };

  const handleTeamChange = (selectedOption: any) => {
    setTeam(selectedOption);
    setValidationErrors((prev) => ({ ...prev, team: undefined }));
  };

  const handleStatusChange = (selectedOption: any) => {
    setAppointmentStatus(selectedOption);
    setValidationErrors((prev) => ({ ...prev, appointmentStatus: undefined }));
  };

  const handleSubjectChange = (selectedOption: any) => {
    if (
      selectedOption &&
      typeof selectedOption === "object" &&
      selectedOption.value
    ) {
      setSubject(selectedOption);
      setValidationErrors((prev) => ({ ...prev, subject: undefined }));
    }
  };

  const handleDateChange = (newDate: any) => {
    setDate(newDate);
    setValidationErrors((prev) => ({ ...prev, date: undefined }));
  };

  const handleBuildingChange = (selectedOption: any) => {
    if (
      selectedOption &&
      typeof selectedOption === "object" &&
      selectedOption.value
    ) {
      setSelectedBuilding(selectedOption.value);
      setFormValues((prevState: any) => ({
        ...prevState,
        contact_name: selectedOption.contact_name || "",
        contact_email: selectedOption.contact_email || "",
        contact_phone: selectedOption.contact_phone || "",
        parking_situation: selectedOption.parking_situation || "",
        additional_information: selectedOption.additional_information || "",
      }));
      if (onSite === null) {
        setOnsite(true);
        setValidationErrors((prev) => ({ ...prev, on_site: undefined }));
      }
    }
  };

  const handleResponsibleUserChange = (
    selectedOption: any,
    fieldName: "executed_by" | "assisted_by" | "on_site",
  ) => {
    if (fieldName === "on_site") {
      if (selectedOption && typeof selectedOption === "object") {
        setOnsite(selectedOption.value);
        setValidationErrors((prev) => ({ ...prev, on_site: undefined }));
      }
      return;
    }
    if (
      selectedOption &&
      typeof selectedOption === "object" &&
      selectedOption.value
    ) {
      if (fieldName === "executed_by") {
        setExecutedBy(selectedOption.value);
      } else setAssistedBy(selectedOption.value);
    }
  };

  const loadExaminationOptions = (inputValue: string): Promise<any[]> => {
    if (!selectedCategory || inputValue.trim().length < 3) {
      return Promise.resolve([]);
    }
    return new Promise((resolve) => {
      api.genericApiRequest({
        entity: "examination",
        method: "get",
        parametersToRender: {
          search: inputValue.trim(),
          additionalUrlParameters: {
            include_relations: true,
            category_set: selectedCategory.value,
          },
        },
        successHandler: (res: any) => {
          const results = res.data.results || [];
          const options = results.map((exam: any) => ({
            value: exam.id,
            label: exam.name,
          }));
          resolve(options);
        },
        failHandler: (error: any) => {
          handleError(error);
          resolve([]);
        },
      });
    });
  };

  const handleCategoryChange = (option: any) => {
    setSelectedCategory(option);
    if (option?.value) {
      setValidationErrors((prev) => ({ ...prev, category: undefined }));
    }
    setSelectedExaminations([]);
  };

  const handleExaminationChange = (option: any) => {
    setSelectedExaminations(Array.isArray(option) ? option : [option]);
    if (Array.isArray(option) && option.length > 0) {
      setValidationErrors((prev) => ({ ...prev, examination: undefined }));
    }
  };

  const handleViewModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newViewMode: "subject" | "company",
  ) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
      if (newViewMode === "company") {
        if (!isCompanySubjectSelection(subject)) {
          setSubject({ value: "", label: "" });
        }
      } else if (newViewMode === "subject") {
        userFormMethods.reset();
      }
    }
  };

  const createAppointment = async (subjectId?: string) => {
    const currentSubjectId = subjectId || subject?.value;
    const examinations = selectedExaminations.map((exam: any) => exam.value);

    try {
      // 1. Create the appointment in draft mode (no building_information)
      const appointmentPayload: any = {
        datetime: date.$d.toISOString(),
        subject: currentSubjectId,
        team: team.value,
        executed_by: executedBy,
        assisted_by: assistedBy,
        category: selectedCategory?.value,
        examination_set: examinations,
        on_site: onSite,
      };

      const response = await new Promise<any>((resolve, reject) => {
        api.genericApiRequest({
          entity: "appointment",
          method: "post",
          submitData: appointmentPayload,
          successHandler: (res: any) => resolve(res),
          failHandler: (error: any) => reject(error),
        });
      });

      const appointment_id = response?.data?.id || response?.id;
      if (!appointment_id) {
        throw new Error(t("error_no_appointment_id_returned"));
      }
      setAppointmentId(appointment_id);

      // 2. If building info is provided, create it and assign via PUT
      if (selectedBuilding) {
        const buildingInfoResult = {
          building: selectedBuilding,
          contact_name: formValues.contact_name,
          contact_phone: formValues.contact_phone,
          contact_email: formValues.contact_email,
          parking_situation: formValues.parking_situation,
          additional_information: formValues.additional_information,
        };

        await new Promise<void>((resolve, reject) => {
          api.genericApiRequest({
            entity: "buildingInformation",
            method: "put",
            entityId: response?.data?.building_information?.id,
            submitData: buildingInfoResult,
            successHandler: () => resolve(),
            failHandler: (error: any) => reject(error),
          });
        });
      }

      // 4. Switch the status of the appointment
      if (appointmentStatus?.value) {
        await new Promise<void>((resolve, reject) => {
          api.genericApiRequest({
            entity: "appointment",
            method: "put",
            entityId: appointment_id,
            submitData: {
              status: appointmentStatus.value,
            },
            successHandler: () => resolve(),
            failHandler: (error: any) => reject(error),
          });
        });
      }

      return appointment_id;
    } catch (error: any) {
      handleError(error);
      throw error;
    } finally {
      setNotificationMessage(t("appointment_created_successfully"));
      setShowSuccessNotification(true);
    }
  };

  const createUser = async (): Promise<string | null> => {
    const userData = userFormMethods.getValues();

    // Build a clean payload for the user creation containing only fields the user actually filled
    const submitData = Object.entries(userData).reduce((acc, [key, val]) => {
      if (val !== undefined && val !== null && val !== "") {
        acc[key] = val;
      }
      return acc;
    }, {} as Record<string, any>);

    // If user is analog, remove email from submitData as it will be generated by backend
    if (submitData.is_analog === true) {
      delete submitData.email;
    }

    const hasUserData =
      submitData &&
      (submitData.first_name ||
        submitData.last_name ||
        (submitData.email && !submitData.is_analog));

    if (!hasUserData) {
      return null;
    }

    try {
      // Add impersonate_subject for department assignment
      if (subject && subject.value) {
        (submitData as any).impersonate_subject = subject.value;
      }

      // Create the user
      const userRes: any = await new Promise((resolve, reject) => {
        api.genericApiRequest({
          entity: "user/create_worker",
          method: "post",
          submitData: submitData,
          successHandler: (res: any) => resolve(res),
          failHandler: (error: any) => reject(error),
        });
      });

      // Verify we got a valid response with subject_id
      const newSubjectId = userRes?.data?.subject_id || userRes?.subject_id;
      if (!newSubjectId) {
        throw new Error(t("error_no_subject_id_returned"));
      }

      // Update the subject state with the new user's information
      setSubject({
        value: newSubjectId,
        label: `${userData.first_name} ${userData.last_name}`,
        subject_id: newSubjectId,
      });

      setNotificationMessage(t("user_created_successfully"));
      setShowSuccessNotification(true);

      return newSubjectId;
    } catch (error: any) {
      handleError(error);
      throw error;
    }
  };

  const validateAllFields = async () => {
    const errors: any = {};
    const invalidTargets: string[] = [];
    const addRequiredError = (target: string) => {
      errors[target] = t("field_required");
      invalidTargets.push(target);
    };

    if (!appointmentStatus || !appointmentStatus.value)
      addRequiredError("appointmentStatus");
    if (!date) addRequiredError("date");
    if (!subject?.value) addRequiredError("subject");

    if (viewMode === "company" && isCompanySubjectSelection(subject)) {
      const userData = userFormMethods.getValues();
      userCreationFields.forEach((field) => {
        if (field.name === "email" && userData.is_analog === true) {
          return;
        }
        if (field.required && !(userData as any)[field.name]) {
          addRequiredError(`user_${field.name}`);
        }
      });
    }

    if (!team || !team.value) addRequiredError("team");
    if (typeof onSite !== "boolean") addRequiredError("on_site");
    if (!selectedCategory) addRequiredError("category");
    if (!selectedExaminations.length) addRequiredError("examination");

    setValidationErrors(errors);
    focusFirstInvalidField({}, invalidTargets);
    return Object.keys(errors).length === 0;
  };

  const appointmentSubjectIsCompany = (): boolean => {
    if (viewMode === "company") {
      return false; // "for new user" mode books the appointment for the person created on submit, not for the chosen company
    }
    if (detailedSubject?.id && subject?.value === detailedSubject.id) {
      return detailedSubject.is_company === true;
    }
    return isCompanySubjectSelection(subject);
  };

  const confirmOccupationalMedicineAppointmentForCompany =
    async (): Promise<boolean> => {
      if (
        !isOccupationalMedicineCategory({
          id: selectedCategory?.value,
          trace_id: selectedCategory?.trace_id,
        }) ||
        !appointmentSubjectIsCompany()
      ) {
        return true;
      }
      return showConfirmationModalAsync(
        t(OCCUPATIONAL_MEDICINE_COMPANY_APPOINTMENT_CONFIRMATION_KEY),
      );
    };

  const handleCreate = async () => {
    setIsBatchCreating(true);

    try {
      const isValid = await validateAllFields();
      if (!isValid) {
        setIsBatchCreating(false);
        return;
      }

      if (!(await confirmOccupationalMedicineAppointmentForCompany())) {
        return;
      }

      const newSubjectId = await createUser();
      const createdAppointmentId = await createAppointment(
        newSubjectId || undefined,
      );
      userFormMethods.reset();
      onClose();

      if (openNewlyCreatedAppointmentInNewTab && createdAppointmentId) {
        const newWindow = window.open("", "_blank");
        if (newWindow) {
          newWindow.location.href = `/appointmentDashboard/${createdAppointmentId}`;
        }
      }

      if (onAppointmentCreated) {
        onAppointmentCreated();
      }
    } catch (error: any) {
      console.error("Creation process failed:", error);
    } finally {
      setIsBatchCreating(false);
      setTimeout(() => {
        setNotificationMessage("");
        setShowSuccessNotification(false);
      }, 3000);
    }
  };

  useEffect(() => {
    if (!open) {
      handleError("");
      setValidationErrors({});
      setViewMode("subject");
      resetForm();
    } else if (
      usePersistedSubjectSearchValue &&
      persistedSubjectSearchValue &&
      Object.keys(persistedSubjectSearchValue).length !== 0
    ) {
      setSubject(persistedSubjectSearchValue);
    }
  }, [open, usePersistedSubjectSearchValue, persistedSubjectSearchValue]);

  const handleUserFieldChange =
    (fieldName: string, onChange: (val: any) => void) => (val: any) => {
      setValidationErrors((prev) => ({
        ...prev,
        [`user_${fieldName}`]: undefined,
      }));
      onChange(val);
    };

  const resetStateOnCloseNotification = () => {
    setShowSuccessNotification(false);
  };

  useModalDismiss(open, onClose);
  const { handleSubmit, isSubmitting } = useFormSubmission({
    onSubmit: handleCreate,
  });

  return (
    <>
      <Modal
        open={open}
        disableAutoFocus
        onClose={(event, reason) => {
          if (reason === "backdropClick") {
            return;
          }
          onClose();
        }}
      >
        <Box className={styles.modalWrapper}>
          <h1 className={styles.heading}>{t("create_new_appointment")}</h1>

          <form onSubmit={handleSubmit} noValidate>
            <div
              className={styles.formRow}
              ref={registerValidationTarget("appointmentStatus")}
            >
              <DropDown
                name="appointmentStatus"
                label={t("status")}
                defaultValue="open"
                options={statusOptions}
                onChange={handleStatusChange}
                required
                mandatory
                wrapperClassName={styles.dropDown__appointmentStatus}
              />
              {validationErrors.appointmentStatus && (
                <span className={styles.validationError}>
                  <ErrorOutlineIcon fontSize="small" />
                  {validationErrors.appointmentStatus}
                </span>
              )}
            </div>

            <div
              className={styles.formRow}
              ref={registerValidationTarget("date")}
            >
              <div className={styles.dateTimePickerLabel}>
                {t("date")}
                <span className={styles.requiredRed}>*</span>
              </div>
              <GenericDateTime
                value={date}
                className={styles.dateTimePicker}
                onChange={(newDate: any) => handleDateChange(newDate)}
                clearable={false}
              />
              {validationErrors.date && (
                <span className={styles.validationError}>
                  <ErrorOutlineIcon fontSize="small" />
                  {validationErrors.date}
                </span>
              )}
            </div>

            <div>
              <div className={styles.formRow}>
                <div>
                  <div className={styles.subjectLabel}>
                    <ToggleButtonGroup
                      value={viewMode}
                      exclusive
                      onChange={handleViewModeChange}
                      size="small"
                    >
                      <ToggleButton value="subject">
                        {t("existing_user_or_company")}
                      </ToggleButton>
                      <ToggleButton value="company">
                        {t("for_new_user")}
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </div>
                  <div ref={registerValidationTarget("subject")}>
                    <FormFieldLabel
                      label={
                        viewMode === "company"
                          ? t("choose_company")
                          : t("choose_existing_user_or_company")
                      }
                      htmlFor="appointment-subject"
                      mandatory
                    />
                    <DropDownSubjectSearch
                      name="appointment-subject"
                      subdomain={subdomain}
                      onChange={handleSubjectChange}
                      wrapperClassName={styles.dropDownSubjectSearch}
                      preExistingValue={
                        subject ||
                        (usePersistedSubjectSearchValue &&
                        persistedSubjectSearchValue &&
                        Object.keys(persistedSubjectSearchValue).length !== 0
                          ? persistedSubjectSearchValue
                          : undefined)
                      }
                      disabled={appointmentStatus?.value !== "open"}
                      includeDeletedSubjects={false}
                      queryParameter={
                        viewMode === "company" ? "companies" : undefined
                      }
                    />
                  </div>
                  {viewMode === "company" &&
                    isCompanySubjectSelection(subject) && (
                      <div className={styles.userCreationFieldsWrapper}>
                        <FormProvider {...userFormMethods}>
                          {userCreationFields.map((field) => {
                            if (
                              field.name === "sex" ||
                              field.name === "is_analog" ||
                              field.name === "preferred_language"
                            ) {
                              return (
                                <div
                                  key={field.name}
                                  className={styles.createUserFormRow}
                                  ref={registerValidationTarget(
                                    `user_${field.name}`,
                                  )}
                                >
                                  <Controller
                                    key={field.name}
                                    name={field.name}
                                    control={userFormMethods.control}
                                    render={({
                                      field: { value, onChange },
                                    }) => {
                                      const options = field.selectOptions ?? [];
                                      const selected = options.filter((o) =>
                                        field.name === "is_analog"
                                          ? String(value) === o.value
                                          : value === o.value,
                                      );

                                      return (
                                        <DropDown
                                          name={field.name}
                                          label={field.label}
                                          options={options}
                                          value={selected}
                                          mandatory={field.required}
                                          required={field.required}
                                          onChange={handleUserFieldChange(
                                            field.name,
                                            (sel) => {
                                              const item = Array.isArray(sel)
                                                ? sel[0]
                                                : sel;
                                              if (!item) return;
                                              const newVal =
                                                field.name === "is_analog"
                                                  ? item.value === "true"
                                                  : item.value;
                                              onChange(newVal);
                                            },
                                          )}
                                          wrapperClassName={styles.dropDown}
                                        />
                                      );
                                    }}
                                  />
                                  {field.required &&
                                    (validationErrors as any)[
                                      `user_${field.name}`
                                    ] && (
                                      <span className={styles.validationError}>
                                        <ErrorOutlineIcon fontSize="small" />
                                        {
                                          (validationErrors as any)[
                                            `user_${field.name}`
                                          ]
                                        }
                                      </span>
                                    )}
                                </div>
                              );
                            }

                            return (
                              <div
                                key={field.name}
                                className={styles.createUserFormRow}
                                ref={registerValidationTarget(
                                  `user_${field.name}`,
                                )}
                              >
                                <CrudField
                                  field={{
                                    id: field.name,
                                    header: field.label,
                                    required: field.required,
                                    selectOptions: field.selectOptions,
                                    multiselect: field.multiselect,
                                    entity: field.entity,
                                    datePicker: field.datePicker,
                                  }}
                                  entityOfTable="user"
                                  handleError={handleSetError}
                                  ignoreGenericCrudSelectMaxWidth={true}
                                  {...(field.name === "department_set" &&
                                  subject &&
                                  subject.value
                                    ? { companySubjectId: subject.value }
                                    : {})}
                                  onChange={handleUserFieldChange(
                                    field.name,
                                    (val) =>
                                      userFormMethods.setValue(
                                        field.name as any,
                                        val as any,
                                      ),
                                  )}
                                />
                                {field.required &&
                                  (validationErrors as any)[
                                    `user_${field.name}`
                                  ] && (
                                    <span className={styles.validationError}>
                                      <ErrorOutlineIcon fontSize="small" />
                                      {
                                        (validationErrors as any)[
                                          `user_${field.name}`
                                        ]
                                      }
                                    </span>
                                  )}
                              </div>
                            );
                          })}
                        </FormProvider>
                      </div>
                    )}
                </div>
                {validationErrors.subject && (
                  <span className={styles.validationError}>
                    <ErrorOutlineIcon fontSize="small" />
                    {validationErrors.subject}
                  </span>
                )}
              </div>

              <div
                className={styles.formRow}
                ref={registerValidationTarget("team")}
              >
                <DropDown
                  defaultValue={team?.value || DEFAULT_KAER_TEAM_ID}
                  name="team"
                  label={t("team")}
                  options={teamOptions}
                  onChange={handleTeamChange}
                  required
                  mandatory
                  wrapperClassName={styles.dropDown__team}
                />
                {validationErrors.team && (
                  <span className={styles.validationError}>
                    <ErrorOutlineIcon fontSize="small" />
                    {validationErrors.team}
                  </span>
                )}
              </div>

              <div className={styles.formRow}>
                <DropDown
                  defaultValue={executedBy || meSubjectDefault}
                  name="executed_by"
                  label={t("executed_by")}
                  options={executedByOptions}
                  onChange={(selectedOption) =>
                    handleResponsibleUserChange(selectedOption, "executed_by")
                  }
                  wrapperClassName={styles.dropDown__executedBy}
                />
              </div>

              <div className={styles.formRow}>
                <DropDown
                  name="assisted_by"
                  label={t("assisted_by")}
                  options={assistedByOptions}
                  onChange={(selectedOption) =>
                    handleResponsibleUserChange(selectedOption, "assisted_by")
                  }
                  wrapperClassName={styles.dropDown__assistedBy}
                />
              </div>
              <div
                className={styles.formRow}
                ref={registerValidationTarget("on_site")}
              >
                <DropDown
                  name="on_site"
                  label={t("on_site")}
                  options={onSiteOptions}
                  externalValue={
                    onSiteOptions.find((option) => option.value === onSite) ??
                    null
                  }
                  onChange={(selectedOption) =>
                    handleResponsibleUserChange(selectedOption, "on_site")
                  }
                  required
                  mandatory
                  wrapperClassName={styles.dropDown__assistedBy}
                />
                {validationErrors.on_site && (
                  <span className={styles.validationError}>
                    <ErrorOutlineIcon fontSize="small" />
                    {validationErrors.on_site}
                  </span>
                )}
              </div>

              <div className={styles.formRow}>
                <DropDown
                  name="building"
                  label={t("Gebäude")}
                  options={buildingOptionsForSubject}
                  onChange={handleBuildingChange}
                  value={
                    buildingOptionsForSubject.find(
                      (b: any) => b.value === selectedBuilding,
                    ) || null
                  }
                  wrapperClassName={styles.dropDown__building}
                />
              </div>
            </div>

            <div
              className={styles.formRow}
              ref={registerValidationTarget("category")}
            >
              <DropDown
                name="category"
                label={t("category")}
                options={categoryOptions}
                onChange={handleCategoryChange}
                value={selectedCategory ? [selectedCategory] : []}
                required
                mandatory
                wrapperClassName={styles.dropDown__appointmentStatus}
              />
              {validationErrors.category && (
                <span className={styles.validationError}>
                  <ErrorOutlineIcon fontSize="small" />
                  {validationErrors.category}
                </span>
              )}
            </div>

            <div
              className={styles.formRow}
              ref={registerValidationTarget("examination")}
            >
              <FormFieldLabel
                label={t("examination")}
                htmlFor="examination"
                mandatory
              />
              <SubjectSearchSelect
                name="examination"
                loadOptions={loadExaminationOptions}
                onChange={handleExaminationChange}
                value={selectedExaminations}
                isMulti
                isClearable
                placeholder={t("search_placeholder")}
                isDisabled={!selectedCategory}
                error={validationErrors.examination}
                additionalWrapperStyles={styles.dropDown__appointmentStatus}
              />
              {validationErrors.examination && (
                <span className={styles.validationError}>
                  <ErrorOutlineIcon fontSize="small" />
                  {validationErrors.examination}
                </span>
              )}
            </div>

            <div className={styles.buttonWrapper}>
              <GenericButton variant="outlined" onClick={onClose}>
                {t("cancel")}
              </GenericButton>
              <GenericButton
                type="submit"
                waitingForResponse={isBatchCreating}
                showSpinnerOnDisable={true}
                loading={isSubmitting}
                disabled={isBatchCreating || isSubmitting}
              >
                {t("create")}
              </GenericButton>
            </div>
          </form>
        </Box>
      </Modal>
      {errorModal}

      <GenericNotification
        message={showSuccessNotification ? notificationMessage : null}
        variant="success"
        handleCloseSnackbar={resetStateOnCloseNotification}
      />
    </>
  );
};

export default SubjectOverviewAppointmentCreateModal;
